/**
 * @module useCheckboxGroup
 * @description Custom hook for managing a group of checkboxes with shared state
 * @note This implementation comes from the DS <7.123.0. The choice to remove this
 * stems from the DS trying to stay unopinionated on state control
 */

import { useState } from 'react';

/**
 * @function useCheckboxGroup
 * @description Manages state for a group of checkbox inputs
 * @param {Array} defaultList - Default list of checked values
 * @returns {Array} - Array containing:
 *   [0] {Array} checkboxList - Current state of checked values
 *   [1] {Function} setCheckboxList - State setter for directly updating checkbox values
 *   [2] {Function} updateCheckboxList - Helper function to add/remove values based on checkbox events
 *   [3] {Function} isCheckboxChecked - Helper function to check if a value is currently selected
 *
 * @example
 * const [
 *   checkedItems,
 *   setCheckedItems,
 *   updateCheckedItems,
 *   isChecked
 * ] = useCheckboxGroup(['option1']);
 *
 * // In your JSX:
 * <input
 *   type="checkbox"
 *   value="option2"
 *   checked={isChecked('option2')}
 *   onChange={(e) => updateCheckedItems({
 *     value: e.target.value,
 *     checked: e.target.checked
 *   })}
 * />
*/
const useCheckboxGroup = (defaultList = []) => {
  const [checkboxList, setCheckboxList] = useState(defaultList);

  /**
   * @function isCheckboxChecked
   * @description Checks if a value exists in the current checkbox list
   * @param {*} value - The value to check
   * @returns {boolean} - True if the value is selected, false otherwise
   */
  const isCheckboxChecked = (value) => checkboxList.includes(value);

  /**
   * @function updateCheckboxList
   * @description Updates the checkbox list based on a checkbox change event
   * @param {Object} options - Update options
   * @param {boolean} options.checked - Whether the checkbox is being checked or unchecked
   * @param {*} options.value - The value to add or remove from the list
   * @returns {Array} - The updated checkbox list
   */
  const updateCheckboxList = ({ checked, value }) => {
    let newList = [...checkboxList];

    if (checked) {
      newList.push(value);
    } else {
      newList = newList.filter((val) => val !== value);
    }

    setCheckboxList(newList);
    return newList;
  };

  return [checkboxList, setCheckboxList, updateCheckboxList, isCheckboxChecked];
};

export default useCheckboxGroup;
